/**
 * Coverage rollups and freshness classification for the /public-records engine.
 *
 * Everything here is derived from the seeded directory data, so the stats strip,
 * the per-state coverage table, and the per-state landing pages all stay in sync
 * with the underlying sources automatically.
 */
import { COUNTY_RECORD_HUBS, type CountyRecordHub } from "@/data/countyRecordHubs";
import { PUBLIC_RECORDS, type PublicRecordSource } from "@/data/publicRecordsDirectory";
import { hubHasGeometry, recordHasGeometry } from "@/lib/public-records-geometry";

/** How recently a county hub's links were human-verified. */
export type Freshness = "fresh" | "aging" | "stale";

/** Verified within this many days counts as fresh. */
export const FRESH_MAX_DAYS = 30;
/** Verified within this many days counts as aging; older is stale. */
export const AGING_MAX_DAYS = 90;

export const FRESHNESS_LABEL: Record<Freshness, string> = {
  fresh: "Recently verified",
  aging: "Verification aging",
  stale: "Verification stale",
};

/** Semantic token classes for the freshness badge. */
export const FRESHNESS_CLASS: Record<Freshness, string> = {
  fresh: "border-brass/30 bg-brass/10 text-brass",
  aging: "border-risk-medium/30 bg-risk-medium/10 text-risk-medium",
  stale: "border-destructive/30 bg-destructive/10 text-destructive",
};

export function daysSince(iso: string, now: Date = new Date()): number {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return Number.POSITIVE_INFINITY;
  return Math.floor((now.getTime() - then) / 86_400_000);
}

export function classifyFreshness(iso: string, now: Date = new Date()): Freshness {
  const days = daysSince(iso, now);
  if (days <= FRESH_MAX_DAYS) return "fresh";
  if (days <= AGING_MAX_DAYS) return "aging";
  return "stale";
}

/** True when a hub's verification is older than the aging threshold. */
export function isStaleHub(hub: CountyRecordHub, now: Date = new Date()): boolean {
  return classifyFreshness(hub.lastVerified, now) === "stale";
}

export function median(values: number[]): number {
  if (!values.length) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2
    ? (sorted[mid] as number)
    : Math.round(((sorted[mid - 1] as number) + (sorted[mid] as number)) / 2);
}

export interface CoverageStats {
  states: number;
  countyHubs: number;
  sources: number;
  /** Sources reachable through a REST/ArcGIS or free GIS endpoint. */
  restSources: number;
  /** Share (0-1) of county hubs publishing spatial layers. */
  geometryShare: number;
  medianReliability: number;
}

export function computeCoverageStats(
  hubs: CountyRecordHub[] = COUNTY_RECORD_HUBS,
  records: PublicRecordSource[] = PUBLIC_RECORDS,
): CoverageStats {
  const states = new Set<string>();
  for (const hub of hubs) states.add(hub.state);
  for (const record of records) if (record.state !== "US") states.add(record.state);
  return {
    states: states.size,
    countyHubs: hubs.length,
    sources: records.length,
    restSources: records.filter(recordHasGeometry).length,
    geometryShare: hubs.length ? hubs.filter(hubHasGeometry).length / hubs.length : 0,
    medianReliability: median(hubs.map((h) => h.reliabilityScore)),
  };
}

export interface StateCoverage {
  code: string;
  name: string;
  countyHubs: number;
  sources: number;
  /** Share (0-1) of that state's county hubs publishing spatial layers. */
  geometryShare: number;
  /** ISO date of the least recently verified hub, or null when no hubs. */
  oldestVerified: string | null;
  medianReliability: number;
}

/** Per-state rollups, sorted by state name. Federal ("US") rows are excluded. */
export function computeStateCoverage(
  hubs: CountyRecordHub[] = COUNTY_RECORD_HUBS,
  records: PublicRecordSource[] = PUBLIC_RECORDS,
): StateCoverage[] {
  const names = new Map<string, string>();
  for (const hub of hubs) names.set(hub.state, hub.stateName);
  for (const record of records) {
    if (record.state !== "US") names.set(record.state, record.stateName);
  }

  return Array.from(names.entries())
    .map(([code, name]) => {
      const stateHubs = hubs.filter((h) => h.state === code);
      const stateRecords = records.filter((r) => r.state === code);
      const verified = stateHubs
        .map((h) => h.lastVerified)
        .sort((a, b) => new Date(a).getTime() - new Date(b).getTime());
      return {
        code,
        name,
        countyHubs: stateHubs.length,
        sources: stateRecords.length,
        geometryShare: stateHubs.length
          ? stateHubs.filter(hubHasGeometry).length / stateHubs.length
          : 0,
        oldestVerified: verified[0] ?? null,
        medianReliability: median(stateHubs.map((h) => h.reliabilityScore)),
      };
    })
    .sort((a, b) => a.name.localeCompare(b.name));
}

export function formatShare(share: number): string {
  return `${Math.round(share * 100)}%`;
}
